import {
  Actions,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { toast } from 'react-hot-toast';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import {
  DiscountUser,
  useDiscountDetailsModel,
} from '../model/DiscountDetails.model';
import {
  DiscountsVM,
  DiscountVMActionMap,
  useDiscountsVM,
} from './discounts.viewModel';
import { ListRow } from '../view/components/GridList';
import { pickObject } from '@client/new/helpers/general';
import {
  filterUsers,
  toBackURL,
  toPerformanceCards,
  toStatusTitle,
  toUserRows,
} from './discountDetails.mappers';
import { Discount } from '../model/Discounts.model';
import { getCSV } from '../model/csv';
import { useState } from 'react';

export const filterTypeOptions = [
  { label: 'All', value: 'All' },
  { label: 'Active Subscriber', value: 'Active Subscriber' },
  { label: 'New', value: 'New' },
  { label: 'Returning', value: 'Returning' },
  { label: 'Returning OTP', value: 'Returning OTP' },
  { label: 'Upgrading', value: 'Upgrading' },
];

type Filters = {
  type: DiscountUser['userType'] | 'All';
};

export type DiscountDetailsVM = {
  search: string;
  filters: Filters;
  userRows: Array<ListRow>;
  performanceCards: Array<ListRow>;
  title: string;
  checkoutURL: string;
  backURL: string;
  statusTitle: string;
  statusType: Discount['status'];
  showDeactivateButton: boolean;
} & Dispatchers<DiscountDetailsVMActionMap> &
  Pick<DiscountsVM, 'deactivationMode'>;

type DiscountDetailsVMActionMap = Pick<
  DiscountVMActionMap,
  'CloseDeactivating' | 'FinishDeactivation'
> & {
  CopyText: string;
  StartDeactivateDiscount: null;
  ExportCSV: null;
  SetFilterType: DiscountUser['userType'];
  SetSearch: string;
};

export type DiscountDetailsAction = Actions<DiscountDetailsVMActionMap>;

export const useDiscountDetailsVM = (discountId: string) => {
  const asyncModel = useDiscountDetailsModel(discountId);
  const discountsVM = useDiscountsVM();

  const [search, setSearch] = useState<DiscountDetailsVM['search']>('');
  const [filters, setFilters] = useState<DiscountDetailsVM['filters']>({
    type: 'All',
  });

  const _dispatch = (a: DiscountDetailsAction) => {
    if (discountsVM.type !== 'fetched') return;
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'CopyText': {
        navigator.clipboard.writeText(a.payload);
        toast.success('Copied');
        return;
      }
      case 'StartDeactivateDiscount':
        return discountsVM.value.dispatch(
          'StartDeactivateDiscount',
          model.discount.discountId,
        );
      case 'ExportCSV':
        return getCSV(model.users, model.discount.name + '.csv');

      case 'CloseDeactivating':
      case 'FinishDeactivation':
        return discountsVM.value.dispatch(a.type);

      case 'SetFilterType':
        setFilters({
          ...filters,
          type: a.payload,
        });
        return;

      case 'SetSearch':
        setSearch(a.payload);
        return;
    }
  };

  const dispatchers = getDispatchers<DiscountDetailsVMActionMap>(_dispatch);

  if (asyncModel.type !== 'fetched') return asyncModel;
  if (discountsVM.type !== 'fetched') return discountsVM;

  const model = asyncModel.value;

  const users = filterUsers(model.users, search, filters);

  return toAsyncFetched<DiscountDetailsVM>({
    showDeactivateButton: model.discount.status === 'Active',
    userRows: toUserRows(users),
    backURL: toBackURL(),
    search,
    filters,
    ...dispatchers,
    checkoutURL: model.discount.url || '',
    performanceCards: toPerformanceCards(model),
    title: model.discount.name,
    statusTitle: toStatusTitle(model.discount.product),
    statusType: model.discount.status,
    ...pickObject(discountsVM.value, ['deactivationMode']),
  });
};
